import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, Loader2 } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { formatUTC } from './utils';
import { formatMoney } from '@/lib/money';
import { FeeBreakdownTable } from './FeeBreakdownTable';

interface CurrentPeriodPreviewProps {
  preview: any;
  isLoading: boolean;
  isFetching: boolean;
  showBreakdown: boolean;
  onToggleBreakdown: () => void;
  isAccountManager: boolean;
  customDateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  onApplyCustomRange: () => void;
  onResetToCurrentWeek: () => void;
  isMounted: boolean;
}

export function CurrentPeriodPreview({
  preview,
  isLoading,
  isFetching,
  showBreakdown,
  onToggleBreakdown,
  isAccountManager,
  customDateRange,
  onDateRangeChange,
  onApplyCustomRange,
  onResetToCurrentWeek,
}: CurrentPeriodPreviewProps) {
  const { t } = useTranslation('billing');
  const isCustomPeriod = Boolean(customDateRange?.from && customDateRange?.to);

  return (
    <Card className="relative">
      <CardHeader>
        {(isLoading || isFetching) && (
          <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center rounded-lg backdrop-blur-sm">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t('currentPeriod.loadingPreview')}</span>
            </div>
          </div>
        )}

        <div className="flex flex-col justify-between gap-6 md:flex-row">
          <div className="flex-1">
            <CardTitle className="mb-2 text-base font-semibold">
              {isCustomPeriod ? t('currentPeriod.titleSelected') : t('currentPeriod.titleCurrent')}
            </CardTitle>
            {preview ? (
              <>
                <p className="text-xl font-bold">
                  {formatUTC(preview.periodStart, 'MMMM d')} - {formatUTC(preview.periodEnd, 'MMMM d, yyyy')}
                </p>
                <p className="text-muted-foreground mt-2 text-sm">
                  {isCustomPeriod
                    ? t('currentPeriod.processingNoteCustom')
                    : t('currentPeriod.processingNoteCurrent')}
                </p>
              </>
            ) : (
              <Skeleton className="h-8 w-64" />
            )}
          </div>
          <div className="flex-1 md:text-right">
            <p className="mb-3 text-sm font-semibold">{t('currentPeriod.transactionsThisPeriod')}</p>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div>
                <p className="text-muted-foreground text-xs">{t('currentPeriod.transactionApproved')}</p>
                <p className="text-lg font-semibold">{preview?.approvedCount || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t('currentPeriod.transactionSubclub')}</p>
                <p className="text-lg font-semibold">{preview?.subclubCount || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t('currentPeriod.transactionDeclined')}</p>
                <p className="text-lg font-semibold">{preview?.declinedCount || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t('currentPeriod.transactionEstFees')}</p>
                <p className="text-lg font-semibold text-green-600">
                  {preview ? formatMoney(preview.estimatedFees, 'USD') : '$0.00'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {isAccountManager && (
          <div className="mt-6 space-y-4 border-t pt-4">
            <div className="space-y-3">
              <label className="block text-sm font-medium">{t('currentPeriod.selectCustomPeriod')}</label>
              <DateRangePicker
                value={customDateRange}
                onChange={onDateRangeChange}
                timezone="UTC"
              />
              {customDateRange?.from && customDateRange?.to && (
                <p className="text-muted-foreground text-sm">
                  {t('currentPeriod.customRangeSummary', {
                    from: formatUTC(customDateRange.from, 'MMM d, yyyy'),
                    to: formatUTC(customDateRange.to, 'MMM d, yyyy'),
                  })}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={onApplyCustomRange}
                disabled={isLoading || !customDateRange?.from || !customDateRange?.to}
              >
                {t('currentPeriod.apply')}
              </Button>
              <Button
                variant="outline"
                onClick={onResetToCurrentWeek}
                disabled={isLoading}
              >
                {t('currentPeriod.resetToCurrentWeek')}
              </Button>
            </div>
          </div>
        )}
      </CardHeader>

      {preview?.feesBreakdown && (
        <CardContent className="border-t">
          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={onToggleBreakdown}
          >
            <ChevronDown className={`mr-2 h-4 w-4 transition-transform ${showBreakdown ? 'rotate-180' : ''}`} />
            {showBreakdown ? t('currentPeriod.hideBreakdown') : t('currentPeriod.showBreakdown')}
          </Button>

          {showBreakdown && (
            <div className="mt-4 overflow-x-auto">
              <FeeBreakdownTable
                breakdown={preview.feesBreakdown}
                totalFees={preview.estimatedFees}
              />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
